import { ProtocolOutDtoWithChecklistId } from 'shared/dtos/out/inspection';
import { ComplianceAnswerOption } from 'shared/domain/compliance/complianceAnswerOption';
import {
  COMPLIANCE_ANSWER_OPTION_PREFIX,
  COMPLIANCE_CHECK_FORM_PREFIX,
} from 'shared/domain/protocolAnswer/formKeys';
import {
  ComplianceCheckModel,
} from 'shared/domain/inspection/inspectionModel';
import { protocolItemsFromForm } from 'shared/domain/inspection/mapping/protocolItemsFromForm';
import { getProtocolItemEkComment } from 'shared/domain/inspection/protocolItemAccessors';
import { DocumentsOnInspectionView } from 'presentation/document/documentsToDocumentsOnInspectionView';
import {
  InspectionOnView,
  ProtocolOnView,
} from 'presentation/inspection/inspectionOnView';
import { HashMap, LabelledEntity } from 'shared/types/commonView';
import { protocolAnswersToFlatFormEntries } from 'shared/domain/inspection/mapping/protocolAnswersToFlatFormEntries';
import {
  buildLegacyComplianceAnswerFormKey,
  buildLegacyProtocolItemCommentFormKey,
  buildProtocolItemCommentFormKey,
  PROTOCOL_SECTION_COMMENT_PREFIX,
} from 'shared/domain/protocolAnswer/formKeys';

// Re-export for backward compatibility
export { PROTOCOL_SECTION_COMMENT_PREFIX };

export interface InspectionFlatForm {
  inspectionDate?: string;
  workTypes?: LabelledEntity[];
  sites?: LabelledEntity[];
  levels?: LabelledEntity[];
  controlledParty?: LabelledEntity[];
  contracts?: LabelledEntity[];
  comment?: string;
  [key: string]: any;
}

export interface ProtocolSectionOutDtoWithSectionId {
  sectionId: string;
  comment: string;
  templateSectionId?: string;
  sectionOrder?: string;
  resolvedSectionId?: boolean;
}

export function toProtocolItems(
  form: InspectionFlatForm
): ProtocolOutDtoWithChecklistId[] {
  return protocolItemsFromForm(form);
}

export function toProtocolSections(
  form: InspectionFlatForm
): ProtocolSectionOutDtoWithSectionId[] {
  const keys = Object.keys(form);
  const sectionPrefix = `${PROTOCOL_SECTION_COMMENT_PREFIX}-`;

  return keys.reduce((sections, key) => {
    if (!key.startsWith(sectionPrefix)) {
      return sections;
    }

    const sectionId = key.slice(sectionPrefix.length);
    if (!sectionId) {
      return sections;
    }

    sections.push({
      sectionId,
      comment: form[key] || '',
      templateSectionId: sectionId,
    });

    return sections;
  }, [] as ProtocolSectionOutDtoWithSectionId[]);
}

export function inspectionToFlatForm(
  inspection: InspectionOnView | undefined,
  inspectionDocuments: DocumentsOnInspectionView
): InspectionFlatForm {
  if (!inspection) {
    return {} as InspectionFlatForm;
  }

  const protocols: HashMap<any> = inspection.protocol.reduce(
    (protocolsObject, protocol) => {
      const checklistItemId = protocol.templateChecklistItem._id;

      Object.keys(protocol.complianceCheck).forEach((key): void => {
        const protocolFormKey = `${COMPLIANCE_CHECK_FORM_PREFIX}-${key}-${checklistItemId}`;
        protocolsObject[protocolFormKey] =
          protocol.complianceCheck[key as keyof ComplianceCheckModel];
      });

      protocolsObject[buildLegacyComplianceAnswerFormKey(checklistItemId)] =
        protocol.complianceCheck.result;

      Object.assign(
        protocolsObject,
        protocolAnswersToFlatFormEntries(checklistItemId, protocol.answers)
      );

      const ekComment = getProtocolItemEkComment(protocol);
      protocolsObject[buildProtocolItemCommentFormKey(checklistItemId)] = ekComment;
      protocolsObject[buildLegacyProtocolItemCommentFormKey(checklistItemId)] =
        ekComment;

      const protocolDocumentsFormKey = `${COMPLIANCE_CHECK_FORM_PREFIX}-documents-${checklistItemId}`;
      protocolsObject[protocolDocumentsFormKey] =
        inspectionDocuments[protocol._id] || [];

      return protocolsObject;
    },
    {} as HashMap<any>
  );

  return {
    comment: inspection.comment,
    contracts: inspection.contracts,
    controlledParty: inspection.controlledParty,
    inspectionDate: inspection.inspectionDate.toISO(),
    levels: inspection.levels,
    sites: inspection.sites,
    workTypes: inspection.workTypes,
    ...(inspection.sections || []).reduce((sectionsObject, section) => {
      const sectionComment = section.comment || '';
      const templateSectionId =
        section.checklistSectionId || section._id;

      sectionsObject[
        `${PROTOCOL_SECTION_COMMENT_PREFIX}-${templateSectionId}`
      ] = sectionComment;

      // Keep backward compatibility for inspections where form keys
      // may still reference protocol section ids.
      sectionsObject[
        `${PROTOCOL_SECTION_COMMENT_PREFIX}-${section._id}`
      ] = sectionComment;
      return sectionsObject;
    }, {} as HashMap<string>),
    ...protocols,
  };
}

export type FormValueToSync = {
  key: string;
  value: ComplianceAnswerOption;
};

export function extractComplianceCheckValuesForSync(
  protocol: ProtocolOnView[],
  previousResults: Map<string, ComplianceAnswerOption>
): FormValueToSync[] {
  const values: FormValueToSync[] = [];

  protocol.forEach((protocolItem) => {
    const checklistItemId = protocolItem.templateChecklistItem._id;
    const resultKey = `${COMPLIANCE_ANSWER_OPTION_PREFIX}-${checklistItemId}`;
    const currentResult = protocolItem.complianceCheck.result;
    const previousResult = previousResults.get(resultKey);

    // Only update if the result in inspection actually changed
    // This prevents overwriting user changes in the form
    if (
      currentResult !== undefined &&
      currentResult !== previousResult
    ) {
      previousResults.set(resultKey, currentResult);
      values.push({ key: resultKey, value: currentResult });
    } else if (
      previousResult === undefined &&
      currentResult !== undefined
    ) {
      // First time setting the value
      previousResults.set(resultKey, currentResult);
      values.push({ key: resultKey, value: currentResult });
    }
  });

  return values;
}

export const SUBMIT_EVENT_TYPE = 'submit-inspection';
export const ATOMIC_INSPECTION_FORM_EVENT_TYPE = 'inspection-atomic-form-noop';
